import { useEffect, useRef, useState } from 'react';
import { getMatch } from './api';
import { DEVICE_PAIRING_EVENT, preferredPairing } from './devicePairing';
import { getPairedYouTubeDevice } from './youtube';
import { loadStoredMatchId, MATCH_STORAGE_KEY } from './matchStorage';

export function useActiveRoom(enabled: boolean, matchId: string | null, onResume: (id: string) => void) {
  const [error, setError] = useState<string | null>(null);
  const resume = useRef(onResume);
  resume.current = onResume;
  const current = useRef(matchId);
  current.current = matchId;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let generation = 0;
    let timer: ReturnType<typeof setTimeout>;
    const check = async (): Promise<void> => {
      clearTimeout(timer);
      if (document.visibilityState === 'hidden') return;
      const request = ++generation;
      const paired = preferredPairing();
      const remembered = loadStoredMatchId();
      try {
        const id = paired
          ? (await getPairedYouTubeDevice(paired.token, paired.deviceId)).device.matchId
          : loadStoredMatchId();
        if (!id || id === current.current) {
          if (!cancelled && request === generation) setError(null);
          return;
        }
        const { state } = await getMatch(id);
        if (cancelled || request !== generation || preferredPairing()?.token !== paired?.token) return;
        if (loadStoredMatchId() !== remembered && loadStoredMatchId() !== id) return;
        setError(null);
        if (!state.practice && (state.status === 'in_progress' || state.status === 'frame_over')) resume.current(id);
      } catch (err) {
        if (!cancelled && request === generation) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled && request === generation) timer = setTimeout(() => void check(), 5000);
      }
    };
    const refresh = (): void => { if (document.visibilityState !== 'hidden') void check(); };
    const storage = (event: StorageEvent): void => {
      if (event.key === null || event.key === MATCH_STORAGE_KEY
        || event.key.startsWith('aiscoreboard.devicePairing.') || event.key === 'aiscoreboard.preferredDeviceId') refresh();
    };
    void check();
    window.addEventListener('storage', storage);
    window.addEventListener('focus', refresh);
    window.addEventListener(DEVICE_PAIRING_EVENT, refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('storage', storage);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(DEVICE_PAIRING_EVENT, refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [enabled]);
  return error;
}
